// The loading card every dashboard custom shows while its first load is in
// flight — On-Track Technologies (2026-09-29).
//
// One design, taken from Budget Control: a card with the site's own logo
// (Settings → Look & Feel; the wordmark when none is set) breathing in the
// middle, a bold line saying what is loading and how far it is, and a
// muted line saying how (one query, two phases, …). Eleven pages used to
// draw eleven different loaders — a plain "Loading…" line, a logo with a
// sweep bar, a logo with a progress bar — and the client asked for one.
//
// Props:
//   ui       the page's theme tokens (fg, muted, border, card, …)
//   S        the page's style map — S.card and S.tiny are used
//   title    the bold line ("Loading budgets and spend — 1 234 so far")
//   note     the muted line under it (optional)
//   percent  0–100 draws a thin progress bar under the caption (optional;
//            a page that knows its total passes it, the others leave it out)
//   style    extra styles for the card (marginBottom, …)
//
// This file sits at customs/ (not customs/shared/) for the reason given in
// standardButtons.js: Work Allocations is bundled with the app's entry, and
// the entry must never import a chunked customs directory.

import { useLookAndFeel } from '@platform/app-shell/frontend/lookAndFeel';

const KEYFRAMES = '@keyframes ott-breathe { 0%,100% { transform: scale(1); opacity: .85 } 50% { transform: scale(1.06); opacity: 1 } }';

export default function LoadingCard({ ui, S, title, note, percent, style }) {
  const lookAndFeel = useLookAndFeel();
  const tiny = (S && S.tiny) || { fontSize: 12, color: ui && ui.muted };
  const card = (S && S.card) || { border: `1px solid ${ui && ui.border}`, borderRadius: 12, background: ui && ui.card, padding: 16 };
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : null;
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '34px 20px', ...(style || {}) }} data-loading-card="">
      <style>{KEYFRAMES}</style>
      <div style={{ animation: 'ott-breathe 1.8s ease-in-out infinite' }}>
        {lookAndFeel.logoDataUri
          ? <img src={lookAndFeel.logoDataUri} alt="" style={{ maxWidth: 240, maxHeight: 64, objectFit: 'contain' }} />
          : <span style={{ fontSize: 22, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: ui && ui.fg }}>On Track</span>}
      </div>
      <div style={{ ...tiny, textAlign: 'center' }}>
        <div style={{ fontWeight: 700, color: ui && ui.fg }}>{title || 'Loading…'}</div>
        {note ? <div>{note}</div> : null}
      </div>
      {pct != null && (
        <div style={{ width: 'min(480px, 100%)', height: 6, borderRadius: 6, background: ui && ui.border, overflow: 'hidden' }}>
          <div style={{ width: `${Math.max(3, pct)}%`, height: '100%', background: ui && (ui.primary || ui.fg), transition: 'width .3s' }} />
        </div>
      )}
    </div>
  );
}
